<template>
  <CCard>
    <CCardHeader class="d-flex justify-content-between">
      <h2 v-if="!id" class="mb-0">New Class</h2>
      <h2 v-else class="mb-0">Edit Class</h2>

      <CSelect
        class="mb-0 mr-3"
        :value="current_language"
        :disabled="loading || loading_data || !id"
        :options="languages"
        @update:value="updateLanguage"
      />
    </CCardHeader>
    <CCardBody>
      <form v-if="(!id || !loading) && !loading_data" autocomplete="off" @submit.prevent="submit">
        <CRow>
          <CCol lg="6">
            <CInput
              v-model="new_class.name"
              label="Title"
              class="mb-3"
              :add-input-classes="{ 'is-invalid': hasError('name') }"
              :invalid-feedback="getError('name')"
            />
            <div class="form-group mb-3" :class="{ 'is-invalid': hasError('instructors') }">
              <label>Instructors</label>
              <multi-select
                :options="dataForSelect(data.instructors)"
                :selected="new_class.instructors"
                :selection="true"
                :search="true"
                options-empty-placeholder="No options"
                search-placeholder="Search"
                selection-type="tags"
                @update="updateData($event, 'instructors')"
              />
              <div v-if="hasError('instructors')" class="invalid-feedback d-block">
                {{ getError('instructors') }}
              </div>
            </div>

            <editor v-model="new_class.description" class="mb-3" />
          </CCol>
          <CCol lg="6">
            <AppSelect
              v-model="new_class.studio"
              :options="data.studios"
              label="Studio"
              placeholder="Select Studio"
              :invalid-feedback="getError('studio')"
            />

            <div class="form-group mb-3" :class="{ 'is-invalid': hasError('categories') }">
              <label>Categories</label>
              <multi-select
                :options="dataForSelect(data.categories)"
                :selected="new_class.categories"
                :selection="true"
                options-empty-placeholder="No options"
                search-placeholder="Search"
                selection-type="tags"
                :search="true"
                @update="updateData($event, 'categories')"
              />
              <div v-if="hasError('categories')" class="invalid-feedback d-block">
                {{ getError('categories') }}
              </div>
            </div>
            <div class="form-group mb-3 s-select" :class="{ 'is-invalid': hasError('tags') }">
              <label>Tags</label>
              <multiselect
                v-model="new_class.tags"
                :options="dataForTagSelect(data.tags)"
                placeholder="Search or add a tag"
                :multiple="true"
                :taggable="true"
                @tag="addTag"
              >
                <template #option="props">
                  <div
                    data-v-6b8bd229=""
                    tabindex="0"
                    class="c-multi-select-option"
                    :class="{
                      'c-multi-selected': new_class.tags.includes(props.option),
                    }"
                  >
                    {{ props.option && props.option.label ? props.option.label : props.option }}
                  </div>
                </template>
              </multiselect>
              <div v-if="hasError('tags')" class="invalid-feedback d-block">
                {{ getError('tags') }}
              </div>
            </div>
            <div class="form-group mb-3">
              <div class="custom-control custom-switch pt-2">
                <input
                  id="isTutorialCheck"
                  v-model="new_class.is_tutorial"
                  type="checkbox"
                  class="custom-control-input"
                />
                <label class="custom-control-label ps-2" style="cursor: pointer" for="isTutorialCheck">
                  Tutorial
                </label>
              </div>
            </div>
            <AppSelect
              v-model="new_class.language"
              :options="data.languages"
              label="Language"
              placeholder="Select Language"
              :invalid-feedback="getError('language')"
            />
            <AppSelect
              v-model="new_class.length"
              :options="data.length"
              label="Length"
              placeholder="Select Length"
              :invalid-feedback="getError('length')"
            />
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-group">
                  <label>Production Date</label>
                  <vc-date-picker
                    v-model="new_class.production_date"
                    mode="dateTime"
                    is24hr
                    :masks="masks"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <input
                        class="form-control"
                        :value="formattedProductionDate"
                        v-on="inputEvents"
                      />
                    </template>
                  </vc-date-picker>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label>Release Date</label>
                  <vc-date-picker
                    v-model="new_class.release_date"
                    mode="dateTime"
                    is24hr
                    :masks="masks"
                  >
                    <template #default="{ inputValue, inputEvents }">
                      <input
                        class="form-control"
                        :value="formattedReleaseDate"
                        v-on="inputEvents"
                      />
                    </template>
                  </vc-date-picker>
                </div>
              </div>
            </div>
            <CInput
              v-model="new_class.vimeo_id"
              label="Vimeo ID"
              :add-input-classes="{ 'is-invalid': hasError('vimeo_id') }"
              :invalid-feedback="getError('vimeo_id')"
            />
          </CCol>
        </CRow>

        <CButton type="submit" color="primary" class="mt-3">Save</CButton>
      </form>
      <div v-else class="mb-3">
        <CSpinner style="width: 2rem; height: 2rem" color="primary" grow />
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import hasApiValidation from '@/mixins/hasApiValidation';
import EditorJodit from '../../components/EditorJodit.vue';
import Multiselect from 'vue-multiselect';
import MultiSelect from '@/components/MultiSelect';
import AppSelect from '@/components/AppSelect.vue';

export default {
  name: 'NewAdmin',
  components: {
    AppSelect,
    MultiSelect,
    editor: EditorJodit,
    Multiselect,
  },
  mixins: [hasApiValidation],
  beforeRouteLeave(to, from, next) {
    if (this.isEdited) {
      const answer = window.confirm(
        'You have unsaved changes. Are you sure you want to leave the page?',
      );
      if (answer) {
        return next();
      } else {
        return next(false);
      }
    } else {
      return next();
    }
  },
  props: ['id'],
  data() {
    return {
      current_language: 'en',
      roles: [],
      new_class: {
        name: '',
        description: '',
        instructors: [],
        tags: [],
        categories: [],
        is_tutorial: false,
        length: '',
        language: '',
        production_date: null,
        studio: '',
        release_date: null,
        vimeo_id: '',
        translations: {},
      },
      originData: {},
      masks: {
        inputDateTime24hr: 'YYYY-MM-DD HH:mm',
      },
      data: {
        instructors: [],
      },
      loading_data: true,
      loading: false,
    };
  },
  computed: {
    languages() {
      return this.$languages;
    },
    formattedProductionDate() {
      return this.$formatDateTime(this.new_class.production_date);
    },
    formattedReleaseDate() {
      return this.$formatDateTime(this.new_class.release_date);
    },
    isEnglish() {
      return this.current_language === 'en';
    },
    isEdited() {
      const parser = (data) => JSON.stringify(data);
      return parser(this.originData) !== parser(this.new_class);
    },
  },
  mounted() {
    this.setOriginData();
    if (this.id) {
      this.loading = true;
      this.fetchClass(this.id);
    }
    this.getData();
  },
  methods: {
    setOriginData() {
      this.originData = JSON.parse(JSON.stringify(this.new_class));
    },
    updateLanguage(value) {
      const prevLanguage = this.current_language;

      if (this.isEdited) {
        const answer = window.confirm(
          'You have unsaved changes! Are you sure you want to leave the page?',
        );
        if (!answer) {
          this.current_language = null;
          this.$nextTick(() => {
            this.current_language = prevLanguage;
          });
          return;
        }
      }

      this.current_language = value;

      const currentLanguageData = this.new_class.translations[value] || {};

      this.new_class.name = currentLanguageData?.name || '';
      this.new_class.description = currentLanguageData?.description || '';

      this.setOriginData();
    },
    setClassData(data) {
      let obj = data.data;
      let isTutorial = false;
      if (obj.class_type === 3 || (obj.class_type && obj.class_type.id === 3)) {
        isTutorial = true;
      }

      this.new_class = {
        ...this.new_class,
        ...obj,
        studio: obj.studio?.id ?? obj.studio,
        is_tutorial: isTutorial,
        translations: {
          ...obj.translations,
          en: {
            name: obj.name,
            description: obj.description,
          },
        },
      };

      this.setOriginData();
    },
    fetchClass(id) {
      this.$http
        .get('/sessions/' + id)
        .then(({ data }) => {
          this.setClassData(data);
        })
        .catch(({ response }) => {
          this.$router.push({ name: 'ClassList' }).then(() => {
            if (response && response.data && response.data.message) {
              this.$toast.error(response.data.message);
            }
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getData() {
      this.loading_data = true;
      this.$http
        .get('/sessions/data')
        .then(({ data }) => {
          this.data = data;
        })
        .finally(() => {
          this.loading_data = false;
        });
    },
    updateData(data, type) {
      this.new_class[type] = data;
    },
    submit() {
      this.setErrors({});

      if (this.id) {
        this.update();
      } else {
        this.save();
      }
    },
    save() {
      this.setOriginData();
      this.$http
        .post('/sessions', this.new_class)
        .then(({ data }) => {
          this.$toast.success('Created');
          const classId = data.data.id;
          this.$router.push({ name: 'Edit Class', params: { id: classId } });
        })
        .catch(({ response }) => {
          this.$toast.error(this.translateError(response.data.message));
          this.setErrors(response.data.errors);
        });
    },
    async update() {
      let ignoreUpdate = false;
      const mainData = this.isEnglish
        ? this.new_class
        : {
            ...this.new_class,
            name: this.new_class.translations?.en.name,
            description: this.new_class.translations?.en.description,
          };

      if (!this.isEnglish) {
        await this.$http
          .post(`/sessions/${this.id}/update-or-create-translations`, {
            language: this.current_language,
            name: this.new_class.name,
            description: this.new_class.description,
          })
          .catch(({ response }) => {
            this.$toast.error(
              this.translateError(response.data.message) ||
                'Error! Something went wrong with translation, please try again',
            );
            this.setErrors(response.data.errors);
            ignoreUpdate = true;
          });
      }

      if (ignoreUpdate) return;
      await this.$http
        .put('/sessions/' + this.id, mainData)
        .then(({ data }) => {
          this.setClassData(data);
          this.updateLanguage(this.current_language);
          this.$toast.success('Updated');
        })
        .catch(({ response }) => {
          this.$toast.error(this.translateError(response.data.message));
          this.setErrors(response.data.errors);
        });
    },
    translateError(message) {
      if (!message) return '';
      const errorMap = {
        'sessions.errors.vimeo_not_available': 'The Vimeo video ID is not available or invalid',
      };
      return errorMap[message] || message;
    },
    dataForSelect(data) {
      return data.map((item) => {
        return {
          value: item.id,
          // label: item.name,
          text: item.name,
        };
      });
    },
    dataForSSelect(data, name = 'Select') {
      let arr = [
        {
          value: '',
          label: name,
          disabled: true,
        },
      ];
      data.forEach((item) => {
        arr.push({
          value: item.id,
          label: item.name,
        });
      });
      return arr;
    },
    dataForTagSelect(data) {
      return data && data.length ? data.map((item) => item.name || item) : [];
    },
    addTag(newTagName) {
      this.data.tags.push({ id: null, name: newTagName });
      this.new_class.tags.push(newTagName);
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.form-group.is-invalid :deep(.c-multi-select) {
  border-color: var(--danger);
}

div.s-select :deep(span.multiselect__option) {
  padding: 0;
  background: transparent !important;
  color: #1a1a1c !important;
  font-size: 14px;
}

div.s-select :deep(span.multiselect__option:after) {
  content: unset !important;
}

div.s-select :deep(.multiselect__option--selected) {
  font-weight: 400;
}

div.s-select :deep(.multiselect__content-wrapper) {
  /* position: absolute; */
  top: 100%;
  left: 0;
  /* display: none; */
  float: left;
  width: 100%;
  padding: 0.5rem 0.75rem;
  /* margin-top: 0.625rem; */
  font-size: 0.875rem;
  text-align: left;
  background-clip: padding-box;
  border-radius: 0.25rem;
  color: #1a1a1c;
  background-color: #fff;
  border-color: #d8dbe0;
  border: 0;
  box-shadow: 0 4px 5px 0 rgba(var(--elevation-base-color), 0.14),
    0 1px 10px 0 rgba(var(--elevation-base-color), 0.12),
    0 2px 4px -1px rgba(var(--elevation-base-color), 0.2);
}

div.s-select :deep(.c-multi-select-option::after),
div.s-select :deep(.c-multi-select-option::before) {
  top: 0.5rem;
}

div.s-select :deep(.multiselect__tag) {
  text-overflow: ellipsis;
  display: inline-block;
  height: 25px;
  margin: 2px;
  border: 1px solid;
  border-radius: 0.25rem;
  background-color: #ebedef;
  border-color: #d8dbe0;
  -webkit-box-flex: 0;
  -ms-flex: 0 0 auto;
  flex: 0 0 auto;
  color: #768192;
}
</style>
